import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Search, User, Phone, Calendar, Clock, X, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { appointmentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const PatientSearch = ({ onSelectAppointment }) => {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAllAppointments();
  }, [currentUser]);

  const loadAllAppointments = async () => {
    setLoading(true);
    try {
      const response = await appointmentsAPI.getAll();
      setAllAppointments(response.data);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las citas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setIsSearching(value.length > 0);

    if (value.trim() === '') {
      setSearchResults([]);
      return;
    }

    const searchLower = value.toLowerCase();
    const results = allAppointments.filter(apt => 
      apt.patientName.toLowerCase().includes(searchLower) ||
      apt.phone.includes(value)
    );

    // Ordenar por fecha más reciente primero
    results.sort((a, b) => {
      const dateA = new Date(a.date + ' ' + a.time);
      const dateB = new Date(b.date + ' ' + b.time);
      return dateB - dateA;
    });

    setSearchResults(results);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setIsSearching(false);
  };

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: { text: 'Confirmada', className: 'bg-green-100 text-green-800 border-green-300' },
      pending: { text: 'Pendiente', className: 'bg-blue-100 text-blue-800 border-blue-300' },
      cancelled: { text: 'Cancelada', className: 'bg-red-100 text-red-800 border-red-300' },
      cancelled_patient: { text: 'Cancelada', className: 'bg-red-100 text-red-800 border-red-300' },
      completed: { text: 'Completada', className: 'bg-purple-100 text-purple-800 border-purple-300' },
      no_show: { text: 'No asistió', className: 'bg-gray-100 text-gray-800 border-gray-300' }
    };
    const variant = variants[status] || variants.pending;
    return <Badge className={variant.className}>{variant.text}</Badge>;
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPP', { locale: es });
    } catch {
      return dateString;
    }
  };

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Agrupar citas por paciente
  const groupByPatient = (appointments) => {
    const grouped = {};
    appointments.forEach(apt => {
      const key = apt.phone; // Usar teléfono como clave única
      if (!grouped[key]) {
        grouped[key] = {
          patientName: apt.patientName,
          phone: apt.phone,
          appointments: []
        };
      }
      grouped[key].appointments.push(apt);
    });
    return Object.values(grouped);
  };

  const patientGroups = isSearching ? groupByPatient(searchResults) : [];

  return (
    <Card className="border-l-4 border-l-green-500">
      <CardHeader className="pb-3 pt-4">
        <CardTitle className="flex items-center text-base">
          <Search className="w-4 h-4 mr-2 text-green-500" />
          Buscar Pacientes
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar por nombre o teléfono..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 pr-10 focus:ring-2 focus:ring-green-500"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1 h-8 w-8 p-0"
              onClick={clearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {isSearching && (
          <div className="mt-4">
            {searchResults.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No se encontraron pacientes</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Se encontraron <strong>{searchResults.length}</strong> cita(s) de <strong>{patientGroups.length}</strong> paciente(s)
                </p>
                {patientGroups.map((group, idx) => (
                  <Card key={idx} className="border border-green-200 bg-green-50/30">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center mb-2">
                            <User className="w-4 h-4 mr-2 text-green-600" />
                            <h3 className="font-semibold text-gray-900">{group.patientName}</h3>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone className="w-3 h-3 mr-2 text-blue-500" />
                            {group.phone}
                          </div>
                        </div>
                        <Badge className="bg-green-600 text-white">
                          {group.appointments.length} cita{group.appointments.length > 1 ? 's' : ''}
                        </Badge>
                      </div>

                      <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                        <div className="flex items-center text-xs font-semibold text-gray-500 mb-2">
                          <FileText className="w-3 h-3 mr-1" />
                          Historial de citas:
                        </div>
                        {group.appointments.map((apt) => {
                          const aptId = apt._id || apt.id;
                          return (
                          <div 
                            key={aptId} 
                            className="p-2 bg-white rounded border hover:border-green-400 transition-colors cursor-pointer"
                            onClick={() => onSelectAppointment && onSelectAppointment(apt)}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex items-center text-sm">
                                <Calendar className="w-3 h-3 mr-1 text-gray-500" />
                                <span className="text-gray-700">{formatDate(apt.date)}</span>
                              </div>
                              {getStatusBadge(apt.status)}
                            </div>
                            <div className="flex items-center text-xs text-gray-600 mb-1">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatTimeTo12Hour(apt.time)}
                            </div>
                            {apt.serviceType && (
                              <Badge className="bg-blue-600 text-white text-xs mb-1">
                                {apt.serviceType}
                              </Badge>
                            )}
                            <p className="text-xs text-gray-600 mt-1">
                              <strong>Motivo:</strong> {apt.reason}
                            </p>
                          </div>
                        )})}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PatientSearch;